import {
  computed, ref, watch, Ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import globalShared from '@/shared';
import {
  getStripeAuthorizeUrl,
  findOnboardingChecklists,
  completeOnboardingTask,
  hideOnboardingChecklist,
  trackHelpEvent,
} from '@/graphs/gql';

export interface ChecklistTask {
  id: string;
  key: string;
  section: string | null;
  title: string;
  tooltip: string | null;
  details: string | null;
  mediaUrl: string | null;
  actionLabel: string | null;
  actionType: string | null;
  actionTarget: string | null;
  isCompleted: boolean;
  completedAt: string | null;
  canMarkComplete: boolean;
}

export interface Checklist {
  id: string;
  key: string;
  title: string;
  subtitle: string | null;
  proTip: string | null;
  completionTitle: string | null;
  completionMessage: string | null;
  completedCount: number;
  totalCount: number;
  isComplete: boolean;
  isHidden: boolean;
  tasks: ChecklistTask[];
}

export interface ResolvedAction {
  type: string;
  target: string;
}

// Used when the API leaves `actionTarget` empty (see FE notes, section 1).
function fallbackAction(key: string, orgId: string): ResolvedAction | null {
  const orgEdit = { type: 'ROUTE', target: `/manage/orgs/${orgId}/edit` };
  const map: { [key: string]: ResolvedAction } = {
    UPLOAD_AVATAR: orgEdit,
    // Key used by the backend's seeded content.
    UPLOAD_AVATAR_BANNER: orgEdit,
    ADD_BIO: orgEdit,
    ADD_VALUES: orgEdit,
    ADD_CAUSE_TAGS: orgEdit,
    CONNECT_STRIPE: { type: 'STRIPE', target: orgId },
    CHECK_BENEVITY: orgEdit,
    ADD_TEAMMATES: orgEdit,
    PREVIEW_PAGE: {
      type: 'URL',
      target: `${window.location.origin}/web/profile/organization/${orgId}`,
    },
    ADD_STICKER: { type: 'ROUTE', target: '/manage-content/media/add-stickers-v2' },
    ADD_FREE_STICKER: { type: 'ROUTE', target: '/manage-content/media/add-stickers-v2' },
    ADD_CAMPAIGN_STICKER: { type: 'ROUTE', target: '/manage-content/media/add-stickers-v2' },
    EMBED_SHOP: { type: 'SIDEBAR', target: 'SHOP_EMBED' },
    CREATE_GLOW: { type: 'ROUTE', target: '/manage-content/media/glows/create' },
    // The rules for these count actions taken as the organization, which only the mobile
    // app can do (CMS and web app have no such flows). Target = what to do there.
    CREATE_GROUP: { type: 'MOBILE_APP', target: 'Create a group.' },
    MAKE_DONATION: { type: 'MOBILE_APP', target: 'Donate to a cause from your wallet.' },
    GIFT_FUNDS: { type: 'MOBILE_APP', target: 'Gift wallet funds to someone.' },
    FOLLOW_SOMEONE: { type: 'MOBILE_APP', target: 'Follow a person or organization.' },
  };
  return map[key] || null;
}

export function trackHelp(input: { [key: string]: any }) {
  const { makeGraphRequest } = globalShared();
  // fire-and-forget: never block the UI or surface errors
  try {
    Promise.resolve(makeGraphRequest('mutation', trackHelpEvent, { input }))
      .catch(() => undefined);
  } catch (e) {
    // ignore
  }
}

export default function useSetupChecklist(
  placement: string,
  orgId: Ref<string | null>,
) {
  const { makeGraphRequest } = globalShared();
  const route = useRoute();
  const router = useRouter();
  const checklist = ref<Checklist | null>(null);
  const loading = ref(false);
  const expandedTaskId = ref<string | null>(null);
  const iframeUrl = ref<string | null>(null);
  const deeplink = ref<string | null>(null);
  const mobileStep = ref<string | null>(null);
  const sidebar = ref<string | null>(null);

  // `silent` refreshes keep the current checklist when the request fails.
  async function load({ silent = false } = {}) {
    if (!orgId.value) {
      checklist.value = null;
      return;
    }
    loading.value = true;
    try {
      const response = await makeGraphRequest(
        'query',
        findOnboardingChecklists,
        { orgId: orgId.value, placement },
      );
      if (silent && response?.errors) return;
      const lists: Checklist[] = response?.data?.findOnboardingChecklists || [];
      checklist.value = response?.errors ? null : lists[0] || null;
      if (checklist.value && expandedTaskId.value === null) {
        const firstOpen = checklist.value.tasks.find((t) => !t.isCompleted);
        expandedTaskId.value = firstOpen ? firstOpen.id : null;
      }
    } catch (e) {
      if (!silent) checklist.value = null;
    } finally {
      loading.value = false;
    }
  }

  const sections = computed(() => {
    const groups: { name: string | null; tasks: ChecklistTask[] }[] = [];
    (checklist.value?.tasks || []).forEach((task) => {
      const name = task.section || null;
      let group = groups.find((g) => g.name === name);
      if (!group) {
        group = { name, tasks: [] };
        groups.push(group);
      }
      group.tasks.push(task);
    });
    return groups;
  });

  function toggleTask(task: ChecklistTask) {
    if (expandedTaskId.value === task.id) {
      expandedTaskId.value = null;
      return;
    }
    expandedTaskId.value = task.id;
    if (orgId.value) {
      trackHelp({
        eventType: 'TIP_OPENED', taskId: task.id, orgId: orgId.value, page: route.path,
      });
    }
  }

  // Same rule the API applies on save; kept for content saved before it did.
  function isSafeAction(type: string, target: string) {
    if (type === 'ROUTE') {
      return target.startsWith('/') && !target.startsWith('//') && !target.includes('\\');
    }
    if (type === 'URL' || type === 'IFRAME') return /^https?:\/\//i.test(target);
    return true;
  }

  function resolveAction(task: ChecklistTask): ResolvedAction | null {
    if (task.actionType && task.actionTarget) {
      return isSafeAction(task.actionType, task.actionTarget)
        ? { type: task.actionType, target: task.actionTarget }
        : null;
    }
    return orgId.value ? fallbackAction(task.key, orgId.value) : null;
  }

  async function markDone(task: ChecklistTask, completed = true) {
    if (!orgId.value) return;
    try {
      const response = await makeGraphRequest(
        'mutation',
        completeOnboardingTask,
        { taskId: task.id, orgId: orgId.value, completed },
      );
      if (response?.errors) throw new Error(response.errors[0].message);
      await load();
    } catch (e) {
      ElMessage.error('Could not update this step. Please try again.');
    }
  }

  async function connectStripe(id: string) {
    try {
      const response = await makeGraphRequest(
        'query',
        getStripeAuthorizeUrl,
        { orgId: id },
      );
      if (response?.errors) throw new Error(response.errors[0].message);
      window.location.href = response.data.getStripeAuthorizeUrl;
    } catch (e) {
      ElMessage.error('Could not start the Stripe connection. Please try again.');
    }
  }

  function runAction(task: ChecklistTask) {
    if (orgId.value) {
      trackHelp({
        eventType: 'ACTION_CLICKED', taskId: task.id, orgId: orgId.value, page: route.path,
      });
    }
    const action = resolveAction(task);
    if (!action) return;
    if (action.type === 'ROUTE') router.push(action.target);
    else if (action.type === 'URL') {
      window.open(action.target, '_blank', 'noopener');
      if (task.canMarkComplete && !task.isCompleted) markDone(task);
    } else if (action.type === 'IFRAME') iframeUrl.value = action.target;
    else if (action.type === 'DEEPLINK') deeplink.value = action.target;
    else if (action.type === 'MOBILE_APP') mobileStep.value = action.target;
    else if (action.type === 'STRIPE') connectStripe(action.target);
    else if (action.type === 'SIDEBAR') sidebar.value = action.target;
  }

  async function setHidden(hidden: boolean) {
    if (!checklist.value || !orgId.value) return;
    try {
      const response = await makeGraphRequest(
        'mutation',
        hideOnboardingChecklist,
        { checklistId: checklist.value.id, orgId: orgId.value, hidden },
      );
      if (response?.errors) throw new Error(response.errors[0].message);
      checklist.value.isHidden = response.data.hideOnboardingChecklist.isHidden;
    } catch (e) {
      ElMessage.error('Could not update the setup assistant. Please try again.');
    }
  }

  // Re-query when the user comes back from an action in another tab/page.
  function onFocus() { load({ silent: true }); }

  watch(orgId, () => load(), { immediate: true });

  return {
    checklist,
    loading,
    sections,
    expandedTaskId,
    iframeUrl,
    deeplink,
    mobileStep,
    sidebar,
    load,
    onFocus,
    toggleTask,
    resolveAction,
    runAction,
    markDone,
    setHidden,
  };
}
